import { Helmet } from 'react-helmet-async';
import {
  Compass, Eye, HeartHandshake, Check, Cpu, Stethoscope,
  BriefcaseBusiness, TrendingUp, Users, BarChart3
} from 'lucide-react';
import { Button, Eyebrow, Reveal, Section, SectionHeader, PageBanner, CTAStrip, LogoMedAliados, LogoMedSenior } from '../components/ui';

export default function QuemSomos() {
  return (
    <main className="page-enter">
      <Helmet>
        <title>Quem Somos | MedAliados — Tecnologia para Corretoras de Saúde Sênior</title>
        <meta name="description" content="Conheça a MedAliados: a plataforma que transforma a operação comercial de corretoras no segmento de saúde sênior em parceria estratégica com a MedSênior. Missão, visão e valores." />
        <link rel="canonical" href="https://medaliados.com.br/quem-somos" />
        <meta property="og:title" content="Quem Somos | MedAliados — Tecnologia para Corretoras de Saúde Sênior" />
        <meta property="og:description" content="Conheça a plataforma que transforma corretoras com tecnologia, dados e a parceria estratégica MedSênior no mercado 49+." />
        <meta property="og:url" content="https://medaliados.com.br/quem-somos" />
      </Helmet>
      <PageBanner
        eyebrow="Quem somos"
        title="A plataforma que transforma a operação comercial de corretoras de saúde."
        sub="Combinamos tecnologia, dados e uma parceria estratégica para entregar previsibilidade e escala no mercado sênior."
      />

      {/* Apresentação */}
      <Section bg="bege" className="py-24 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6 text-[1.05rem] leading-relaxed text-texto-escuro">
            <Reveal>
              <p>A <b className="text-verde-escuro">MedAliados</b> é uma plataforma digital criada para transformar a operação comercial de corretoras. Nascemos da observação de um problema crônico do mercado: o talento comercial existe, a demanda existe, mas a infraestrutura para conectar os dois é frágil.</p>
            </Reveal>
            <Reveal delay={80}>
              <p>Nossa <b className="text-verde-escuro">missão</b> é empoderar corretoras com tecnologia, dados e estrutura para escalar no mercado de saúde sênior — segmento de maior crescimento demográfico e maior potencial econômico do país nos próximos quinze anos.</p>
            </Reveal>
            <Reveal delay={140}>
              <p>Nossa <b className="text-verde-escuro">visão</b> é construir a maior rede de corretoras parceiras do segmento sênior no Brasil, operando com previsibilidade industrial e produtos de altíssima demanda.</p>
            </Reveal>
            <Reveal delay={200}>
              <p className="text-texto-medio">Para isso, escolhemos um caminho diferente: foco vertical, parceria estratégica única e uma plataforma desenhada para o ciclo completo de venda, do lead à carteira ativa.</p>
            </Reveal>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              { Icon: Compass, t: "Missão", d: "Empoderar corretoras com tecnologia e dados para escalar no mercado de saúde sênior.", dark: false },
              { Icon: Eye, t: "Visão", d: "Ser a maior rede de corretoras parceiras do segmento sênior no Brasil.", dark: false },
              { Icon: HeartHandshake, t: "Valores", d: "Foco, ética comercial, previsibilidade e crescimento compartilhado com cada corretora parceira.", dark: true },
            ].map((c, i) => (
              <Reveal key={i} delay={100 + i * 60}>
                <div className={`rounded-2xl p-7 ${c.dark ? "bg-verde-escuro text-fundo-branco" : "bg-fundo-branco border border-borda"}`}>
                  <c.Icon size={22} className={c.dark ? "text-verde-lima" : "text-verde-medio"} />
                  <h3 className={`font-display font-semibold text-[1.18rem] mt-3 ${c.dark ? "" : "text-verde-escuro"}`}>{c.t}</h3>
                  <p className={`text-[0.96rem] mt-2 leading-relaxed ${c.dark ? "text-fundo-branco/75" : "text-texto-medio"}`}>{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* PARCERIA */}
      <Section bg="branco" className="py-24 md:py-32">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <Eyebrow>A parceria estratégica</Eyebrow>
            <h2 className="font-display font-bold h-section mt-4 text-verde-escuro">MedAliados + MedSênior: a parceria que faz a diferença</h2>
            <p className="mt-5 text-[1.05rem] text-texto-medio leading-relaxed">
              Trazemos a inteligência de conversão. A MedSênior, operadora especializada no público 49+, traz décadas de expertise em produtos de saúde sênior com forte presença no mercado brasileiro.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-2 gap-6">
          {[
            { logo: "aliados", title: "A tecnologia de conversão", items: ["Plataforma digital ponta a ponta", "Inteligência de vendas em tempo real", "Geração e qualificação de leads", "Gestão integrada da carteira"] },
            { logo: "senior", title: "A expertise em saúde sênior", items: ["Portfólio especializado no público 49+", "Forte presença regional consolidada", "Produtos com competitividade comercial", "Estrutura assistencial robusta"] },
          ].map((block, bi) => (
            <Reveal key={bi} delay={bi * 100}>
              <div className="rounded-2xl border border-borda bg-fundo-bege p-8 h-full">
                <div className="flex items-center gap-3">
                  {block.logo === "aliados" ? <LogoMedAliados /> : <LogoMedSenior />}
                </div>
                <h3 className="font-display font-bold text-[1.4rem] text-verde-escuro mt-6">{block.title}</h3>
                <ul className="mt-5 space-y-3 text-texto-medio">
                  {block.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-verde-medio text-fundo-branco shrink-0">
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* EQUAÇÃO DO SUCESSO */}
      <Section bg="bege" className="py-24 md:py-32">
        <Reveal>
          <SectionHeader align="center" eyebrow="A equação do sucesso"
            title="Três forças se somam em um único resultado"
            sub="Nenhum dos componentes, isoladamente, entrega o que entregamos juntos."
          />
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-7 gap-4 items-stretch">
          {[
            { t: "MedAliados", sub: "A tecnologia de conversão", Icon: Cpu },
            { t: "MedSênior", sub: "A expertise em saúde", Icon: Stethoscope },
            { t: "Sua Corretora", sub: "O talento comercial", Icon: BriefcaseBusiness },
          ].map((b, i) => (
            <div key={i} className="contents">
              {i > 0 && <div className="hidden lg:flex items-center justify-center font-display font-bold text-[3rem] text-verde-medio">+</div>}
              <div className="lg:col-span-1">
                <Reveal delay={i * 100}>
                  <div className="rounded-2xl bg-fundo-branco border border-borda p-6 h-full text-center">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-verde-escuro text-fundo-branco mx-auto">
                      <b.Icon size={20} />
                    </span>
                    <h3 className="font-display font-bold text-[1.18rem] text-verde-escuro mt-4">{b.t}</h3>
                    <p className="text-[0.9rem] text-texto-medio mt-1">{b.sub}</p>
                  </div>
                </Reveal>
              </div>
            </div>
          ))}
          <div className="hidden lg:flex items-center justify-center font-display font-bold text-[3rem] text-dourado">=</div>
          <div className="lg:col-span-1">
            <Reveal delay={400}>
              <div className="rounded-2xl bg-verde-escuro text-fundo-branco p-6 h-full text-center border border-verde-escuro">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-dourado text-verde-escuro mx-auto">
                  <TrendingUp size={20} />
                </span>
                <h3 className="font-display font-bold text-[1.18rem] mt-4">Crescimento escalável</h3>
                <p className="text-[0.9rem] text-fundo-branco/70 mt-1">+ previsibilidade financeira</p>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="lg:hidden mt-8 text-center font-display font-bold text-2xl text-verde-medio">
          MedAliados + MedSênior + Sua Corretora <span className="text-dourado">= Escala</span>
        </div>
      </Section>

      {/* MERCADO 49+ */}
      <Section bg="branco" className="py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <Eyebrow>Oportunidade demográfica</Eyebrow>
              <h2 className="font-display font-bold h-section mt-4 text-verde-escuro">O mercado que mais cresce no Brasil</h2>
              <p className="mt-5 text-[1.05rem] text-texto-medio leading-relaxed">
                A população 49+ é o segmento de maior crescimento demográfico do país. Combinada com aumento da expectativa de vida e maior cuidado com saúde preventiva, configura uma demanda estrutural de longo prazo por planos especializados.
              </p>
              <p className="mt-4 text-[1.05rem] text-texto-medio leading-relaxed">
                Para corretoras que dominam esse vertical, a vantagem é dupla: ticket médio superior e carteira com maior estabilidade ao longo do tempo.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            {[
              { v: "63M", l: "Brasileiros com 49+ em 2030" },
              { v: "2,8×", l: "Crescimento vs. 2000" },
              { v: "+41%", l: "Demanda por planos sênior" },
              { v: "R$ 38b", l: "Mercado anual estimado" },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="rounded-xl border border-borda bg-fundo-bege p-6">
                  <div className="font-display font-bold text-[2.4rem] text-verde-medio leading-none">{s.v}</div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-texto-medio mt-3">{s.l}</div>
                </div>
              </Reveal>
            ))}
            <div className="col-span-2 text-[0.78rem] font-mono text-texto-medio/80">
              [fonte: projeções demográficas IBGE — dados ilustrativos]
            </div>
          </div>
        </div>
      </Section>

      <CTAStrip headline="Construa seu lugar no maior mercado dos próximos 15 anos." sub="Cadastre sua corretora e converse com nosso time comercial." label="Cadastrar Corretora" />
    </main>
  );
}
